import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Breadcrumbs from '../components/Breadcrumbs';
import ProductPanel from '../components/ProductPanel';
import { StoreContext } from '../context';
import { addProduct, getTotals } from '../redux/cartRedux';
import { addFavorite } from '../redux/favRedux';

const CATEGORIES = ['bedroom', 'office', 'kitchen', 'living room', 'kids', 'dining'];

export default function Products() {
  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState(1);
  const cart = useSelector((state) => state.cart);

  const {
    query,
    setQuery,
    categoryIndex,
    setCategoryIndex,
    price,
    setPrice,
    sortByIndex,
    setSortByIndex,
    viewGrid,
    setViewGrid,
    products,
    clearFilters,
  } = useContext(StoreContext);

  const params = useLocation();

  const filteredProducts =
    products &&
    products.filter((product) => {
      if (
        (query.trim() !== '' && !product.title.includes(query.trim().toLowerCase())) ||
        (categoryIndex > -1 &&
          categoryIndex < 6 &&
          product.category !== CATEGORIES[categoryIndex]) ||
        product.price > price
      )
        return false;
      return true;
    });

  const sortProducts = (products, index) => {
    if (index < 0 || index > 4) return products;
    switch (index) {
      case 0:
        return products.sort((a, b) => a.price - b.price);
      case 1:
        return products.sort((a, b) => a.price - b.price).reverse();
      case 2:
        return products.sort((a, b) => {
          const aName = a.title.toLowerCase();
          const bName = b.title.toLowerCase();
          if (aName < bName) return -1;
          if (aName > bName) return 1;
          return 0;
        });
      case 3:
        return products
          .sort((a, b) => {
            const aName = a.title.toLowerCase();
            const bName = b.title.toLowerCase();
            if (aName < bName) return -1;
            if (aName > bName) return 1;
            return 0;
          })
          .reverse();
      default:
        return products;
    }
  };

  useEffect(() => {
    dispatch(getTotals());
  }, [cart, dispatch]);

  const handleAddCart = (product, color, size) => {
    const { desc, categories, ...item } = product;
    dispatch(addProduct({ ...item, quantity, color, size }));
  };

  const handleAddFav = (product, color, size) => {
    const { desc, categories, ...item } = product;
    dispatch(addFavorite({ ...item, quantity, color, size }));
  };

  return (
    <>
      <Breadcrumbs>
        <Link to="/">Home</Link>
        <span>Products</span>
      </Breadcrumbs>
      <section>
        <div className="tw-container py-10 flex flex-col md:flex-row gap-10">
          <section id="filters">
            <div className="sticky top-10 flex flex-col gap-4">
              {/* Search Input */}
              <input
                type="text"
                placeholder="Search"
                value={query}
                className="bg-gray-200 w-5/12 md:w-full py-1 px-2 rounded"
                onChange={(e) => setQuery(e.target.value)}
              />
              {/* Categories */}
              <div id="categories">
                <h5>Category</h5>
                <ul className="mt-2">
                  <li>
                    <button
                      onClick={() => setCategoryIndex(-1)}
                      className={`${categoryIndex === -1 ? 'underline' : ''}`}
                    >
                      All
                    </button>
                  </li>
                  {CATEGORIES.map((category, i) => (
                    <li key={i} className="capitalize">
                      <button
                        onClick={() => setCategoryIndex(i)}
                        className={`${categoryIndex === i ? 'underline' : ''}`}
                      >
                        {category}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Price */}
              <div id="price-range">
                <h5>Price</h5>
                <p className="mt-2">${price}</p>
                <input
                  type="range"
                  min={0}
                  max={309999}
                  onChange={(e) => setPrice(parseInt(e.currentTarget.value))}
                  value={price}
                />
              </div>
              <button className="btn-sm bg-red-300 text-red-900 font-bold" onClick={clearFilters}>
                Clear Filters
              </button>
            </div>
          </section>
          <section id="products" className="w-full">
            <article
              id="list-header"
              className={'flex flex-col md:grid md:items-center gap-y-2 gap-x-6'}
              style={{ gridTemplateColumns: 'auto auto 1fr auto' }}
            >
              <div className="btn-container">
                <button
                  className={`border border-black rounded w-6 md:w-7 p-1 ${
                    viewGrid ? 'bg-black text-white' : ''
                  }`}
                  onClick={() => setViewGrid(true)}
                >
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M1 2.5A1.5 1.5 0 012.5 1h3A1.5 1.5 0 017 2.5v3A1.5 1.5 0 015.5 7h-3A1.5 1.5 0 011 5.5v-3zm8 0A1.5 1.5 0 0110.5 1h3A1.5 1.5 0 0115 2.5v3A1.5 1.5 0 0113.5 7h-3A1.5 1.5 0 019 5.5v-3zm-8 8A1.5 1.5 0 012.5 9h3A1.5 1.5 0 017 10.5v3A1.5 1.5 0 015.5 15h-3A1.5 1.5 0 011 13.5v-3zm8 0A1.5 1.5 0 0110.5 9h3a1.5 1.5 0 011.5 1.5v3a1.5 1.5 0 01-1.5 1.5h-3A1.5 1.5 0 019 13.5v-3z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </button>
                <button
                  className={`border border-black rounded w-6 md:w-7 p-1 ${
                    !viewGrid ? 'bg-black text-white' : ''
                  }`}
                  onClick={() => setViewGrid(false)}
                >
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.5 11.5A.5.5 0 013 11h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5zm0-4A.5.5 0 013 7h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5zm0-4A.5.5 0 013 3h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </button>
              </div>
              <p>{filteredProducts && filteredProducts.length} Products Found</p>
              <hr />
              <form>
                <label>Sort By: </label>
                <select
                  className="px-2 py-1"
                  value={sortByIndex}
                  onChange={(e) => setSortByIndex(parseInt(e.target.value))}
                >
                  <option value={0}>Price (Lowest)</option>
                  <option value={1}>Price (Highest)</option>
                  <option value={2}>Name (A-Z)</option>
                  <option value={3}>Name (Z-A)</option>
                </select>
              </form>
            </article>
            <article id="products-list">
              <div
                className={`bg-gray-100 p-3 mt-6 ${
                  viewGrid ? 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4' : ''
                }`}
              >
                {sortProducts(filteredProducts, sortByIndex).map((product) => (
                  <ProductPanel
                    key={product._id}
                    product={product}
                    grid={viewGrid}
                    handleAddCart={handleAddCart}
                    handleAddFav={handleAddFav}
                  />
                ))}
              </div>
            </article>
          </section>
        </div>
      </section>
    </>
  );
}
